import { createContext, useContext, useMemo, useRef } from 'react';

import type { ReactNode } from 'react';
import type { Action } from './reducer/types';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface WidgetEvents {}

export type WidgetEvent = {
  [K in keyof WidgetEvents]: Action<K, WidgetEvents[K]>;
}[keyof WidgetEvents];

export type OnCallback = ((event: WidgetEvent) => void) | undefined;

export type EventContextValue = {
  dispatch: (event: WidgetEvent) => void;
  dispatchOnce: (id: string, event: WidgetEvent) => void;
};

export const EventContext = createContext<EventContextValue>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  dispatch: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  dispatchOnce: () => {},
});

export const useEventPipeline = (): EventContextValue => useContext(EventContext);

export type EventPipelineProps = {
  children: ReactNode;
  on: OnCallback;
};

export const EventsProvider = ({ children, on }: EventPipelineProps): JSX.Element => {
  const triggeredEventsRef = useRef<Record<string, boolean>>({});

  const dispatch = (event: WidgetEvent): void => {
    on?.(event);
  };
  const dispatchOnce = (id: string, event: WidgetEvent): void => {
    if (triggeredEventsRef.current[id]) return;
    triggeredEventsRef.current[id] = true;
    on?.(event);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const contextValue = useMemo(() => ({ dispatch, dispatchOnce }), [on]);

  return <EventContext.Provider value={contextValue}>{children}</EventContext.Provider>;
};

export const useEventProvider = (): EventContextValue => {
  const context = useContext(EventContext);

  if (context === null) {
    throw new Error('The useEventProvider must be used within the EventProvider');
  }

  return context;
};
